import React, { useCallback, useState } from 'react';
import { trpc } from '@/utils/trpc-client';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { PageTitle } from '@/features/shared/page-title';
import { Channel, Platform, Rate, Tactic } from 'shared/src/domain/line-item/line-item-channels';
import {
  AlertTriangleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  XIcon
} from 'lucide-react';
import { showErrorToast } from '@/components/feedback/error-toast';
import { toast } from 'react-toastify';
import { Button } from '@/components/forms/ui/button';
import { Input } from '@/components/forms/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/forms/ui/card';
import clsx from 'clsx';
import { RateCardHistory } from './rate-card-history';
import {
  adjustForCPM,
  undoCPM
} from '@/features/line-items/line-item-form/utils/line-item-form-utils';

type Tab = 'rates' | 'history';

export function RateCardAdmin() {
  const { channels } = useChannelRatesContext();
  const { data: allPlatforms } = trpc.allPlatforms.useQuery();
  const [activeTab, setActiveTab] = useState<Tab>('rates');
  const [expandedChannels, setExpandedChannels] = useState<Set<number>>(new Set());
  const [expandedTactics, setExpandedTactics] = useState<Set<number>>(new Set());

  const toggleChannel = (id: number) => {
    setExpandedChannels(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTactic = (id: number) => {
    setExpandedTactics(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-6">
      <PageTitle title="Rate Card Admin" />
      <h1 className="mb-2 text-2xl font-bold">Rate Card Admin</h1>
      <p className="mb-4 text-muted-foreground">
        Manage channel tactics, platforms, and rates. Changes take effect immediately.
      </p>
      <div className="mb-4 flex gap-4">
        <button
          onClick={() => setActiveTab('rates')}
          className={clsx(
            'rounded-md px-3 py-1.5 text-sm font-medium',
            activeTab === 'rates'
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          )}>
          Rates
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={clsx(
            'rounded-md px-3 py-1.5 text-sm font-medium',
            activeTab === 'history'
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          )}>
          History
        </button>
      </div>
      {activeTab === 'rates' ? (
        <div className="space-y-2">
          {channels.map(channel => (
            <ChannelSection
              key={channel.id}
              channel={channel}
              allPlatforms={allPlatforms ?? []}
              isExpanded={expandedChannels.has(channel.id)}
              onToggle={() => toggleChannel(channel.id)}
              expandedTactics={expandedTactics}
              onToggleTactic={toggleTactic}
            />
          ))}
        </div>
      ) : (
        <RateCardHistory />
      )}
    </div>
  );
}

function ChannelSection({
  channel,
  allPlatforms,
  isExpanded,
  onToggle,
  expandedTactics,
  onToggleTactic
}: {
  channel: Channel;
  allPlatforms: Platform[];
  isExpanded: boolean;
  onToggle: () => void;
  expandedTactics: Set<number>;
  onToggleTactic: (id: number) => void;
}) {
  return (
    <Card>
      <CardHeader
        onClick={onToggle}
        className="flex cursor-pointer flex-row items-center justify-between py-4 hover:bg-muted/50">
        <CardTitle className="text-lg">{channel.name}</CardTitle>
        {isExpanded ? (
          <ChevronDownIcon className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="h-5 w-5 text-muted-foreground" />
        )}
      </CardHeader>
      {isExpanded && (
        <CardContent className="border-t pt-4">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-muted-foreground">
                <th className="pb-2 pr-4">Tactic</th>
                <th className="pb-2 pr-4">Platforms</th>
                <th className="pb-2">Rates</th>
              </tr>
            </thead>
            <tbody>
              {channel.tactics.map(tactic => (
                <TacticRow
                  key={tactic.id}
                  tactic={tactic}
                  allPlatforms={allPlatforms}
                  isExpanded={expandedTactics.has(tactic.id)}
                  onToggle={() => onToggleTactic(tactic.id)}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      )}
    </Card>
  );
}

function TacticRow({
  tactic,
  allPlatforms,
  isExpanded,
  onToggle
}: {
  tactic: Tactic;
  allPlatforms: Platform[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const utils = trpc.useUtils();
  const [editingPlatforms, setEditingPlatforms] = useState(false);
  const [selectedPlatformIds, setSelectedPlatformIds] = useState<number[]>(
    tactic.platforms.map(p => p.id)
  );
  const [removalWarning, setRemovalWarning] = useState<{
    platformIds: number[];
    platformNames: string[];
  } | null>(null);
  const [pendingPlatformIds, setPendingPlatformIds] = useState<number[] | null>(null);

  const lineItemsQuery = trpc.lineItemsUsingTacticPlatforms.useQuery(
    { tacticId: tactic.id, platformIds: removalWarning?.platformIds ?? [] },
    { enabled: removalWarning !== null && removalWarning.platformIds.length > 0 }
  );

  const updatePlatformsMutation = trpc.updateTacticPlatforms.useMutation({
    onSuccess: () => {
      utils.channels.invalidate();
      setEditingPlatforms(false);
      setRemovalWarning(null);
      setPendingPlatformIds(null);
      toast.success('Platforms updated', toastProps);
    },
    onError: () => showErrorToast()
  });

  const handleSavePlatforms = useCallback(async () => {
    const currentPlatformIds = tactic.platforms.map(p => p.id);

    const sameLength = currentPlatformIds.length === selectedPlatformIds.length;
    const sameContent = currentPlatformIds.every(id => selectedPlatformIds.includes(id));
    if (sameLength && sameContent) {
      setEditingPlatforms(false);
      return;
    }

    const removedPlatformIds = currentPlatformIds.filter(id => !selectedPlatformIds.includes(id));

    if (removedPlatformIds.length > 0 && !removalWarning) {
      const removedPlatformNames = removedPlatformIds.map(
        id => allPlatforms.find(p => p.id === id)?.name ?? 'Unknown'
      );
      setPendingPlatformIds(selectedPlatformIds);
      setRemovalWarning({
        platformIds: removedPlatformIds,
        platformNames: removedPlatformNames
      });
      return;
    }

    updatePlatformsMutation.mutate({
      tacticId: tactic.id,
      platformIds: selectedPlatformIds
    });
  }, [
    selectedPlatformIds,
    allPlatforms,
    removalWarning,
    updatePlatformsMutation,
    tactic.platforms,
    tactic.id
  ]);

  const handleConfirmRemoval = () => {
    if (pendingPlatformIds) {
      updatePlatformsMutation.mutate({
        tacticId: tactic.id,
        platformIds: pendingPlatformIds
      });
    }
  };

  const handleCancelRemoval = () => {
    setRemovalWarning(null);
    setPendingPlatformIds(null);
    setSelectedPlatformIds(tactic.platforms.map(p => p.id));
  };

  const handleCancelPlatforms = () => {
    setSelectedPlatformIds(tactic.platforms.map(p => p.id));
    setEditingPlatforms(false);
    setRemovalWarning(null);
    setPendingPlatformIds(null);
  };

  const togglePlatform = (platformId: number) => {
    setSelectedPlatformIds(prev =>
      prev.includes(platformId) ? prev.filter(id => id !== platformId) : [...prev, platformId]
    );
  };

  return (
    <>
      <tr className="border-t border-border">
        <td className="py-3 pr-4 align-top">{tactic.name}</td>
        <td className="py-3 pr-4 align-top">
          {editingPlatforms ? (
            <div className="space-y-2">
              <div className="flex max-h-48 flex-wrap gap-1 overflow-y-auto rounded border border-border bg-muted p-2">
                {allPlatforms.map(platform => (
                  <label
                    key={platform.id}
                    className={`cursor-pointer rounded px-2 py-1 text-xs ${
                      selectedPlatformIds.includes(platform.id)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-accent'
                    }`}>
                    <input
                      type="checkbox"
                      checked={selectedPlatformIds.includes(platform.id)}
                      onChange={() => togglePlatform(platform.id)}
                      className="sr-only"
                    />
                    {platform.name}
                  </label>
                ))}
              </div>
              {removalWarning ? (
                <PlatformRemovalWarning
                  platformNames={removalWarning.platformNames}
                  activeCount={lineItemsQuery.data?.activeCount ?? 0}
                  pastCount={lineItemsQuery.data?.pastCount ?? 0}
                  isLoading={lineItemsQuery.isLoading}
                  isPending={updatePlatformsMutation.isPending}
                  onConfirm={handleConfirmRemoval}
                  onCancel={handleCancelRemoval}
                />
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleSavePlatforms}
                    disabled={updatePlatformsMutation.isPending}
                    size="sm"
                    className="bg-green-50 text-green-700 hover:bg-green-100 border border-green-200">
                    <CheckIcon className="h-3 w-3" />
                    {updatePlatformsMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button onClick={handleCancelPlatforms} variant="outline" size="sm">
                    <XIcon className="h-3 w-3" /> Cancel
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <div className="flex flex-wrap gap-1">
                {tactic.platforms.map(platform => (
                  <span
                    key={platform.id}
                    className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {platform.name}
                  </span>
                ))}
              </div>
              <Button
                onClick={() => setEditingPlatforms(true)}
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-blue-50 hover:text-blue-600">
                <PencilIcon className="h-4 w-4" />
              </Button>
            </div>
          )}
        </td>
        <td className="py-3 align-top">
          <Button
            onClick={onToggle}
            variant="ghost"
            size="sm"
            className="text-primary hover:text-primary/80">
            {tactic.rates.length} rates
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </Button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={3} className="pb-4 pl-8">
            <RatesTable tacticId={tactic.id} rates={tactic.rates} />
          </td>
        </tr>
      )}
    </>
  );
}

function PlatformRemovalWarning({
  platformNames,
  activeCount,
  pastCount,
  isLoading,
  isPending,
  onConfirm,
  onCancel
}: {
  platformNames: string[];
  activeCount: number;
  pastCount: number;
  isLoading: boolean;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const platformList = platformNames.join(', ');
  const hasActiveLineItems = activeCount > 0;
  const hasPastLineItems = pastCount > 0;

  if (isLoading) {
    return (
      <div className="rounded border border-border bg-muted/50 p-3">
        <p className="text-xs text-muted-foreground">Checking for affected line items...</p>
      </div>
    );
  }

  if (hasActiveLineItems) {
    return (
      <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
        <div className="mb-2 flex items-center gap-2 text-destructive">
          <AlertTriangleIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Cannot Remove Platform(s)</span>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">
          <span className="text-destructive font-medium">{activeCount} active line item(s)</span>{' '}
          use {platformNames.length === 1 ? 'this platform' : 'these platforms'}.
        </p>
        <p className="mb-3 text-xs text-muted-foreground">
          Platforms: <strong>{platformList}</strong>
        </p>
        <Button onClick={onCancel} variant="outline" size="sm">
          <XIcon className="h-3 w-3" /> Close
        </Button>
      </div>
    );
  }

  if (hasPastLineItems) {
    return (
      <div className="rounded border border-amber-500/50 bg-amber-500/10 p-3">
        <div className="mb-2 flex items-center gap-2 text-amber-600">
          <AlertTriangleIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Platform Removal Warning</span>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">
          <span className="text-amber-600 font-medium">{pastCount} past line item(s)</span> use{' '}
          {platformNames.length === 1 ? 'this platform' : 'these platforms'}. These line items will
          remain unchanged.
        </p>
        <p className="mb-3 text-xs text-muted-foreground">
          Platforms: <strong>{platformList}</strong>
        </p>
        <div className="flex gap-2">
          <Button onClick={onConfirm} disabled={isPending} variant="destructive" size="sm">
            <CheckIcon className="h-3 w-3" />
            {isPending ? 'Confirming...' : 'Confirm Removal'}
          </Button>
          <Button onClick={onCancel} variant="outline" size="sm">
            <XIcon className="h-3 w-3" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-muted/50 p-3">
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">
        <AlertTriangleIcon className="h-4 w-4" />
        <span className="text-sm font-medium">Confirm Platform Removal</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Are you sure you want to remove <strong>{platformList}</strong> from this tactic?
      </p>
      <div className="flex gap-2">
        <Button onClick={onConfirm} disabled={isPending} variant="destructive" size="sm">
          <CheckIcon className="h-3 w-3" />
          {isPending ? 'Confirming...' : 'Confirm'}
        </Button>
        <Button onClick={onCancel} variant="outline" size="sm">
          <XIcon className="h-3 w-3" /> Cancel
        </Button>
      </div>
    </div>
  );
}

function RatesTable({ tacticId, rates }: { tacticId: number; rates: Rate[] }) {
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="p-2">Unit Price Type</th>
              <th className="p-2">Target Margin</th>
              <th className="p-2">Unit Price</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map(rate => (
              <RateRow key={rate.unitPriceType.id} tacticId={tacticId} rate={rate} />
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function RateRow({ tacticId, rate }: { tacticId: number; rate: Rate }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [targetMargin, setTargetMargin] = useState(rate.targetMargin.toString());
  const [unitPrice, setUnitPrice] = useState(
    adjustForCPM(rate.unitPrice, rate.unitPriceType).toString()
  );

  const updateRateMutation = trpc.updateRate.useMutation({
    onSuccess: () => {
      utils.channels.invalidate();
      setEditing(false);
      toast.success('Rate updated', toastProps);
    },
    onError: () => showErrorToast()
  });

  const handleSave = () => {
    const marginVal = parseFloat(targetMargin);
    const priceVal = parseFloat(unitPrice);

    if (isNaN(marginVal) || marginVal < 0 || marginVal > 1) {
      toast.error('Target margin must be between 0 and 1', toastProps);
      return;
    }
    if (isNaN(priceVal) || priceVal < 0) {
      toast.error('Unit price must be zero or greater', toastProps);
      return;
    }

    const originalPrice = adjustForCPM(rate.unitPrice, rate.unitPriceType);
    if (marginVal === rate.targetMargin && priceVal === originalPrice) {
      setEditing(false);
      return;
    }

    updateRateMutation.mutate({
      tacticId,
      unitPriceTypeId: rate.unitPriceType.id,
      targetMargin: marginVal,
      unitPrice: undoCPM(priceVal, rate.unitPriceType)
    });
  };

  const handleCancel = () => {
    setTargetMargin(rate.targetMargin.toString());
    setUnitPrice(adjustForCPM(rate.unitPrice, rate.unitPriceType).toString());
    setEditing(false);
  };

  return (
    <tr className="border-t border-border">
      <td className="p-2 text-sm">{rate.unitPriceType.name}</td>
      <td className="p-2">
        {editing ? (
          <Input
            type="text"
            value={targetMargin}
            onChange={e => setTargetMargin(e.target.value)}
            className="h-8 w-20"
          />
        ) : (
          <span className="text-sm text-muted-foreground">{rate.targetMargin * 100}%</span>
        )}
      </td>
      <td className="p-2">
        {editing ? (
          <Input
            type="text"
            value={unitPrice}
            onChange={e => setUnitPrice(e.target.value)}
            className="h-8 w-24"
          />
        ) : (
          <span className="text-sm text-muted-foreground">
            ${adjustForCPM(rate.unitPrice, rate.unitPriceType)}
          </span>
        )}
      </td>
      <td className="p-2">
        {editing ? (
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={updateRateMutation.isPending}
              size="icon"
              className="h-8 w-8 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200">
              {updateRateMutation.isPending ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
            </Button>
            <Button onClick={handleCancel} variant="outline" size="icon" className="h-8 w-8">
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setEditing(true)}
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-blue-50 hover:text-blue-600">
            <PencilIcon className="h-4 w-4" />
          </Button>
        )}
      </td>
    </tr>
  );
}

const toastProps = {
  autoClose: 3000,
  closeOnClick: true,
  style: { width: 'auto', padding: '10px 20px' }
};
